import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useBrand } from '../context/BrandContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  FileText, 
  Image, 
  Volume2, 
  TrendingUp,
  Calendar,
  Clock,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { currentBrand } = useBrand();
  const { user } = useAuth();
  const [stats, setStats] = useState({ total_scripts: 0, total_images: 0, total_audios: 0, total_content: 0 });
  const [recentContent, setRecentContent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, contentRes] = await Promise.all([
        axios.get(`${API}/stats`, { withCredentials: true }),
        axios.get(`${API}/content?limit=5`, { withCredentials: true })
      ]);
      setStats(statsRes.data || { total_scripts: 0, total_images: 0, total_audios: 0, total_content: 0 });
      // Ensure recentContent is always an array
      const content = contentRes.data;
      setRecentContent(Array.isArray(content) ? content : []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setRecentContent([]);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Scripts', value: stats.total_scripts, icon: FileText, color: currentBrand.primary },
    { label: 'Images', value: stats.total_images, icon: Image, color: '#10B981' },
    { label: 'Audios', value: stats.total_audios, icon: Volume2, color: '#3B82F6' },
    { label: 'Total Content', value: stats.total_content, icon: TrendingUp, color: '#EF4444' },
  ];

  const quickActions = [
    { label: 'Create Script', path: '/scripts', icon: FileText, description: 'Generate AI scripts' },
    { label: 'Create Image', path: '/images', icon: Image, description: 'Generate thumbnails' },
    { label: 'Create Audio', path: '/audio', icon: Volume2, description: 'Text to speech' },
  ];

  const getContentIcon = (type) => {
    switch (type) {
      case 'script': return FileText;
      case 'image': return Image;
      case 'audio': return Volume2;
      default: return FileText;
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col lg:flex-row lg:items-center justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold text-zinc-100 font-['Manrope']">
            Welcome back, {user?.name?.split(' ')[0] || 'Creator'}
          </h1>
          <p className="text-zinc-400 mt-1">
            Currently creating content for <span style={{ color: currentBrand.primary }}>{currentBrand.name}</span>
          </p>
        </div>
        <div 
          className="px-4 py-2 rounded-xl border flex items-center gap-2"
          style={{ borderColor: `${currentBrand.primary}40`, background: `${currentBrand.primary}10` }}
        >
          <Calendar className="w-4 h-4" style={{ color: currentBrand.primary }} />
          <span className="text-sm" style={{ color: currentBrand.primary }}>
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </span>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
            className="p-6 rounded-xl bg-zinc-900/50 backdrop-blur-xl border border-zinc-800 hover:border-zinc-700 transition-all duration-300"
          >
            <div className="flex items-center justify-between mb-4">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ background: `${stat.color}20` }}
              >
                <stat.icon className="w-6 h-6" style={{ color: stat.color }} />
              </div>
            </div>
            <p className="text-3xl font-bold text-zinc-100 font-['Manrope']">{stat.value}</p>
            <p className="text-sm text-zinc-500 mt-1">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-2 p-6 rounded-xl bg-zinc-900/50 backdrop-blur-xl border border-zinc-800"
        >
          <h2 className="text-xl font-bold text-zinc-100 mb-6 font-['Manrope']">Quick Actions</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {quickActions.map((action) => (
              <Link 
                key={action.path} 
                to={action.path}
                data-testid={`quick-action-${action.path.slice(1)}`}
                className="group p-5 rounded-xl border border-zinc-800 hover:border-zinc-700 transition-all duration-300 hover:-translate-y-1"
                style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, transparent 100%)' }}
              >
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: `${currentBrand.primary}20` }}
                >
                  <action.icon className="w-6 h-6" style={{ color: currentBrand.primary }} />
                </div>
                <h3 className="font-medium text-zinc-100 mb-1">{action.label}</h3>
                <p className="text-sm text-zinc-500">{action.description}</p>
                <div className="flex items-center gap-1 mt-3 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: currentBrand.primary }}>
                  Create Now <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="p-6 rounded-xl bg-zinc-900/50 backdrop-blur-xl border border-zinc-800"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-zinc-100 font-['Manrope']">Recent</h2>
            <Link 
              to="/library" 
              className="text-sm hover:underline"
              style={{ color: currentBrand.primary }}
            >
              View All
            </Link>
          </div>
          
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3 animate-pulse">
                  <div className="w-10 h-10 rounded-lg bg-zinc-800" />
                  <div className="flex-1">
                    <div className="h-4 bg-zinc-800 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-zinc-800 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : !Array.isArray(recentContent) || recentContent.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-zinc-500">No content yet</p>
              <p className="text-sm text-zinc-600 mt-1">Create your first content!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentContent.map((content) => {
                const ContentIcon = getContentIcon(content.type);
                return (
                  <div 
                    key={content.id}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-zinc-800/30 transition-colors cursor-pointer"
                  >
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `${currentBrand.primary}20` }}
                    >
                      <ContentIcon className="w-5 h-5" style={{ color: currentBrand.primary }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-zinc-200 truncate">{content.title}</p>
                      <div className="flex items-center gap-2 text-xs text-zinc-500">
                        <Clock className="w-3 h-3" />
                        {new Date(content.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>
      </div>

      {/* Brand Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="p-6 rounded-xl border border-zinc-800"
        style={{ background: `linear-gradient(135deg, ${currentBrand.primary}08 0%, transparent 100%)` }}
      >
        <h2 className="text-xl font-bold text-zinc-100 mb-4 font-['Manrope']">Current Brand Context</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <p className="text-sm text-zinc-500 mb-1">Selected Brand</p>
            <p className="text-lg font-medium text-zinc-100">{currentBrand.name}</p>
            <p className="text-sm text-zinc-400 mt-1">{currentBrand.tagline}</p>
          </div>
          <div>
            <p className="text-sm text-zinc-500 mb-1">Tip</p>
            <p className="text-sm text-zinc-400">
              Switch brands from the sidebar to create content tailored to each of your websites. 
              The AI will automatically adjust tone and style.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
